import { Link as RouterLink } from 'react-router-dom';
import {
  ICandidateFilter,
  IPostgreAnalyticsFilter,
  IPostgreJobsAnalyticsPayload,
  ISubmissionAnalyticsPayload,
} from 'wisestep-api';
import { WsColors } from 'wisestep-base';

import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { JobsWidget, Widget } from '../DerivedCharts/helper';
import { getDateRangeByFrequency } from '../utils/timeInfo';

import { getEncodedJobFilters } from './jobs/utils/jobsHelper';
import { encodeSubmissionFilters } from './submissions';

export const defaultWidgetData = {
  count: 0,
  response: [],
  data: [],
  categories: [],
  series: [],
  resultSet: [],
};

export type chartSeriesDataType = {
  x: string;
  y: number;
  fillColor?: string;
  strokeColor?: string;
  meta?: Record<string, any>;
  goals?: any;
};
type queryType = ISubmissionAnalyticsPayload | IPostgreJobsAnalyticsPayload;
type widgetNameType = JobsWidget | Widget;

export const navigateToListingPage = (
  barData: chartSeriesDataType,
  analyticsGlobalFilter: IPostgreAnalyticsFilter[],
  filterNameValuePair: [string, string],
  query: queryType
) => {
  const barFilter = analyticsGlobalFilter.filter(
    (analyticsFilter) =>
      analyticsFilter.filterName != 'DATE_RANGE' &&
      analyticsFilter.filterName != filterNameValuePair[0]
  );
  const dateRange = getDateRangeByFrequency(barData.x, query.frequency as string);
  barFilter.push({
    filterName: 'DATE_RANGE',
    values: [
      JSON.stringify({
        filterBy: '',
        fromDate: dateRange.fromDateTime,
        toDate: dateRange.toDateTime,
      }),
    ],
  });
  barFilter.push({
    filterName: filterNameValuePair[0],
    values: [filterNameValuePair[1]],
  });

  if (query.category == 'submission' || query.category == 'businessDefinition')
    openUrlInNewTab(`/all-submissions?search=${encodeSubmissionFilters(barFilter, query)}`);
  else if (query.category == 'job')
    openUrlInNewTab(`/jobs?d=${getEncodedJobFilters(barFilter, query)}`);
};

export const barChartData = (widgetName: string, widgetData, query: queryType) => {
  const barData = { categories: [] as string[], series: [] as ApexAxisChartSeries };
  let stacked = false;
  if (!widgetName.includes('stack')) {
    barData.categories = widgetData.response?.map((record) => record.startTime as string) ?? [];
    const bucketData = widgetData.response?.map((record) => ({
      x: record.startTime,
      y: record.count ?? 0,
    }));
    barData.series = [
      {
        data: bucketData ?? [],
        name: query.category,
      },
    ];
  } else {
    barData.categories = widgetData.categories as string[];
    barData.series = widgetData.series as ApexAxisChartSeries;
    stacked = true;
  }

  return { barData, stacked };
};

export const getFilterNameValue = (
  widgetName: widgetNameType,
  chartSeries: chartSeriesDataType
): [string, string] => {
  switch (widgetName) {
    case JobsWidget.STACKED_BAR_CHART_CLIENT:
    case Widget.STACKEDBAR_CLIENTS:
      return ['CLIENT_NAMES', chartSeries.meta?.clientName];
    case JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT:
      return ['CLIENT_RECRUITERS', chartSeries.meta?.clientContactEmail];
    case JobsWidget.STACKED_BAR_CHART_STATUS_WISE:
      return ['JOB_STATUS', chartSeries.meta?.jobStatus];
    case JobsWidget.STACKED_BAR_CHART_TYPE_WISE:
      return ['JOB_TYPE', chartSeries.meta?.jobType];
    default:
      return ['', ''];
  }
};

export const enum RTRAnalyticsWidget {
  RTR_SPLIT_METRIC = 'rtr-split-metric',
  RTR_RECRUITER_TABLE = 'rtr-recruiter-table',
  RTR_STACKED_BAR_CHART = 'rtr-stacked-bar-chart',
  RTR_JOB_TITLE_TABLE = 'rtr-job-title-table',
}

export const renderCellFor = (entity: string, params, customValue?: string) => {
  let url = '';
  switch (entity) {
    case 'ACCOUNT_MANAGER':
      url = `/account-manager/details-page?id=${
        params.row['accountManagerEmail'] || params.row['account_manager_email']
      }&name=${params.value}`;
      break;
    case 'RECRUITER':
      url = `/users/details-page?email=${params.row['recruiterEmail']}&isLink=true&isUserAnalytics=true`;
      break;
    case 'RECRUITER_ID':
      url = `/users/details-page?email=${
        params.row['recruiterId'] || params.row['recruiter_id']
      }&isLink=true&isUserAnalytics=true`;
      break;
    case 'CLIENT':
      url = `/client-details?name=${
        params.row['clientName'] || params.row['company_name']
      }&isChild=true&clientId=${params.row['clientId']}&jobCode=${
        params.row['jobCode'] || params.row['job_code']
      }&tabIndex=0&breadCrumbList=${getEncodedQueryParams(ClintPageBreadCrumb['CLIENTS'])}`;
      break;
    case 'CLIENT_RECRUITER':
      url = `/${params.row['clientContactId']}/client-contact-details?name=${params.row['clientName']}&isChild=true`;
      break;
    case 'JOB':
      url = `/jobs-crm/${params.row['jobId']}/false/0/pipeline`;
      break;
    case 'TEAM':
      url = `/team/team-members-view?teamId=${params.row['teamId']}&teamName=${params.row['teamName']}`;
      break;
    case 'CANDIDATE':
      url = `/candidate-layout/submissions?id=${params.row['candidateId']}`;
      break;
    default:
      url = '/home';
  }

  const isValueMissing = params.value == null || params.value == '__MISSING__';

  return (
    <RouterLink
      to={url}
      target="_blank"
      style={{
        textDecoration: 'none',
        color: WsColors.TheBlues700,
        ...(isValueMissing &&
          !customValue && { pointerEvents: 'none', color: WsColors.GenericRichBlack }),
      }}
    >
      {customValue ?? (isValueMissing ? 'Not Available' : params.value)}
    </RouterLink>
  );
};

export const overrideAnalyticsFilter = (
  analyticsFilter: ICandidateFilter[],
  filterNameValuePairs: [string, string][]
) => {
  const newFilterList = analyticsFilter.filter(
    (filter) => !filterNameValuePairs.map(([filterName]) => filterName).includes(filter.filterName)
  );
  filterNameValuePairs.forEach(([filterName, filterValue]) =>
    newFilterList.push({
      filterName,
      values: Array.isArray(filterValue) ? filterValue : [filterValue],
    })
  );
  return newFilterList;
};
